// FilterDialog — the centered modal shell the board filter panels open in.
//
// One wrapper for Discover and Trenches, copied from SearchModal's mechanics so
// "opens like the search dialog" is literal: a native <dialog> via showModal()
// (top layer, focus trap, Escape -> close), margin:auto centering, dim
// backdrop, and pointer-dismiss on the backdrop. The panel content stays a
// plain component; only placement lives here.

import { type ReactEventHandler, type ReactNode, useEffect, useRef } from 'react'
import styles from './filterDialog.module.css'

interface Props {
  /** Accessible name for the dialog ("Trenches filters", "Discover filters"). */
  label: string
  onClose: () => void
  /** The native cancel (Escape, a back gesture) before the dialog shuts: a
   *  dialog that must stay open prevents it. */
  onCancel?: ReactEventHandler<HTMLDialogElement>
  /** While true, a native close the page could not cancel (Chrome lets a page
   *  refuse one back gesture; a second one shuts a modal <dialog> anyway) is
   *  undone: the dialog is shown again. The <dialog> never stays shut under a
   *  page that still holds it open — scroll-locked, its opener dead. */
  holdOpen?: boolean
  children: ReactNode
}

export function FilterDialog({ label, onClose, onCancel, holdOpen = false, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const downOnBackdrop = useRef(false)
  // the element that had focus when the dialog opened (never one inside it)
  const opener = useRef<HTMLElement | null>(null)

  // Mounted only while open, so showModal on mount is the whole lifecycle.
  // Body scroll locks for the same reason SearchModal locks it: the backdrop
  // dims the page, and a page that still scrolls under a dimmed veil reads as
  // broken.
  //
  // Unmounting an open dialog does not hand focus back (only a native close()
  // does), so a Close button or a backdrop click left keyboard focus on <body>;
  // the opener is remembered once and refocused on every way out.
  useEffect(() => {
    const d = ref.current
    const active = document.activeElement
    // StrictMode re-runs this effect after showModal moved focus inside: keep
    // the first, outside opener
    if (!opener.current && active instanceof HTMLElement && !d?.contains(active)) {
      opener.current = active
    }
    if (d && !d.open) d.showModal()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
      // Refocus once the dialog has left the DOM: while a modal dialog is open
      // the rest of the page is inert and refuses focus. Never close() it here —
      // its close event would run onClose, and StrictMode's re-run of this
      // effect would then shut the dialog the moment it opened.
      // The one case where a dialog is still open and the opener can take the
      // focus: the opener stands IN that dialog — this one was opened over it
      // (Trade settings over the Search modal or over the Page settings).
      const back = opener.current
      if (!back) return
      window.setTimeout(() => {
        if (!back.isConnected) return
        if (back.closest('dialog[open]') || !document.querySelector('dialog[open]')) back.focus()
      }, 0)
    }
  }, [])

  return (
    // Click on the dialog element itself = the ::backdrop area -> close (the
    // panel fills the dialog box, so a click that targets the dialog can only
    // have landed outside it). Keyboard dismiss is the native Escape.
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop pointer-dismiss; keyboard close is native Escape
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-label={label}
      onCancel={onCancel}
      onClose={() => {
        const d = ref.current
        if (holdOpen && d && !d.open) {
          d.showModal()
          return
        }
        onClose()
      }}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === ref.current
      }}
      onClick={(e) => {
        if (e.target === ref.current && downOnBackdrop.current) onClose()
      }}
    >
      {children}
    </dialog>
  )
}
